/**
 * useScopeTrainingAnalytics — Round 75 (A-C-05)
 *
 * Read-only scope-aware training VISIBILITY (not effectiveness).
 * Built on the Round 73/74 in-scope foundation. Strictly independent
 * from Connect, scheduling, talent analytics, and Pensieve.
 *
 * Returns:
 *   - summary:     aggregate usage metrics across the scope
 *   - artifacts:   per-artifact usage breakdown
 *   - technicians: per-technician engagement rows
 *
 * NEVER joins training_outcome_windows / training_job_outcomes /
 * training_effect_signals. This round counts activity only —
 * effectiveness modeling is queued for Round 76.
 *
 * Gated by `canViewInScopeTalent` from useEnterpriseScope.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useEnterpriseScope } from './useEnterpriseScope';
import type { InScopeScopeType } from './useInScopeTalent';

export interface ScopeTrainingSummary {
  total_artifacts_used: number;
  total_training_events: number;
  unique_technicians_engaged: number;
  avg_artifacts_per_technician: number | null;
  top_artifact_type: string | null;
  computed_at: string;
}

export interface ScopeTrainingArtifactRow {
  artifact_id: string;
  artifact_title: string;
  artifact_type: string;
  provider_name: string;
  total_uses: number;
  unique_technicians: number;
  last_used_at: string | null;
}

export interface ScopeTechnicianTrainingRow {
  user_id: string;
  display_name: string;
  shop_id: string;
  shop_name: string;
  training_events_count: number;
  unique_artifacts_used: number;
  last_training_at: string | null;
}

export interface ScopeTrainingFilters {
  shop_ids?: string[];
  artifact_type?: string;
  search?: string;
}

interface UseScopeTrainingAnalyticsArgs {
  scopeType: InScopeScopeType | null;
  scopeId: string | null;
  filters?: ScopeTrainingFilters;
}

export function useScopeTrainingAnalytics({
  scopeType,
  scopeId,
  filters,
}: UseScopeTrainingAnalyticsArgs) {
  const { canViewInScopeTalent } = useEnterpriseScope();
  const enabled = canViewInScopeTalent && !!scopeType && !!scopeId;

  // Server-side filters via p_filters jsonb. Empty fields are stripped.
  const cleanFilters = filters
    ? Object.fromEntries(
        Object.entries(filters).filter(([, v]) =>
          Array.isArray(v) ? v.length > 0 : !!v,
        ),
      )
    : {};

  const summaryQuery = useQuery({
    queryKey: ['scope-training-summary', scopeType, scopeId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_scope_training_summary', {
        p_scope_type: scopeType!,
        p_scope_id: scopeId!,
      });
      if (error) throw error;
      const row = (data ?? [])[0] as ScopeTrainingSummary | undefined;
      return row ?? null;
    },
    enabled,
  });

  const artifactsQuery = useQuery({
    queryKey: ['scope-training-breakdown', scopeType, scopeId, cleanFilters],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_scope_training_breakdown', {
        p_scope_type: scopeType!,
        p_scope_id: scopeId!,
        p_filters: cleanFilters as any,
      });
      if (error) throw error;
      return (data ?? []) as ScopeTrainingArtifactRow[];
    },
    enabled,
  });

  const techFilters = filters
    ? Object.fromEntries(
        Object.entries({ shop_ids: filters.shop_ids, search: filters.search }).filter(
          ([, v]) => (Array.isArray(v) ? v.length > 0 : !!v),
        ),
      )
    : {};

  const techniciansQuery = useQuery({
    queryKey: ['scope-training-technicians', scopeType, scopeId, techFilters],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_scope_technician_training_usage', {
        p_scope_type: scopeType!,
        p_scope_id: scopeId!,
        p_filters: techFilters as any,
      });
      if (error) throw error;
      return (data ?? []) as ScopeTechnicianTrainingRow[];
    },
    enabled,
  });

  return {
    summary: summaryQuery.data ?? null,
    artifacts: artifactsQuery.data ?? [],
    technicians: techniciansQuery.data ?? [],
    isLoading:
      summaryQuery.isLoading || artifactsQuery.isLoading || techniciansQuery.isLoading,
    error: (summaryQuery.error ?? artifactsQuery.error ?? techniciansQuery.error) as
      | Error
      | null,
  };
}
